// src/components/PayableBills.jsx
import React, { useEffect, useMemo, useState } from 'react';
import '../styles/PayableBills.css';

import {
  createPayableBill,
  getPayableBills,
  updatePayableBill,
  togglePayableBill,
  getUpcoming,
  getDashboardWeek,
  getDashboardRange,
  payBillPayment,
  skipBillPayment,
} from '../services/payableBillsService';

const TABS = {
  UPCOMING: 'upcoming',
  WEEK: 'week',
  MONTH: 'month',
  RULES: 'rules',
};

const DOW = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' },
];

function isoToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function firstDayOfMonthISO(anyISO) {
  const [y, m] = anyISO.split('-').map(Number);
  return `${y}-${String(m).padStart(2, '0')}-01`;
}

function lastDayOfMonthISO(anyISO) {
  const [y, m] = anyISO.split('-').map(Number);
  const last = new Date(y, m, 0).getDate(); // JS: day 0 del sig. mes = último del actual
  return `${y}-${String(m).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
}

function formatMoney(amount, currency = 'CRC') {
  const n = Number(amount || 0);
  try {
    if (currency === 'CRC') {
      return new Intl.NumberFormat('es-CR', { style: 'currency', currency: 'CRC' }).format(n);
    }
    return new Intl.NumberFormat('es-CR', { style: 'currency', currency }).format(n);
  } catch {
    return `${currency} ${n.toFixed(2)}`;
  }
}

function statusLabel(status) {
  switch (status) {
    case 'paid':
      return 'Pagado';
    case 'pending':
      return 'Pendiente';
    case 'overdue':
      return 'Vencida';
    case 'skipped':
      return 'Omitida';
    default:
      return status || '';
  }
}

function statusClass(status) {
  switch (status) {
    case 'paid':
      return 'pb-badge pb-paid';
    case 'pending':
      return 'pb-badge pb-pending';
    case 'overdue':
      return 'pb-badge pb-overdue';
    case 'skipped':
      return 'pb-badge pb-skipped';
    default:
      return 'pb-badge';
  }
}

const emptyBillForm = {
  name: '',
  provider: '',
  reference: '',
  amount: '',
  currency: 'CRC',
  scheduleType: 'monthly',
  dueDate: '',
  dayOfWeek: 1,
  dayOfMonth: 1,
  anchorDate: '',
  notes: '',
  isActive: true,
};

export default function PayableBills() {
  const [tab, setTab] = useState(TABS.UPCOMING);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // data
  const [payments, setPayments] = useState([]);
  const [rules, setRules] = useState([]);

  // filters
  const [upcomingDays, setUpcomingDays] = useState(14);
  const [weekDate, setWeekDate] = useState(isoToday());
  const [monthDate, setMonthDate] = useState(isoToday());

  // modals
  const [billModalOpen, setBillModalOpen] = useState(false);
  const [billEditing, setBillEditing] = useState(null);
  const [billForm, setBillForm] = useState({ ...emptyBillForm });

  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null); // BillPayment row
  const [payForm, setPayForm] = useState({
    paidAmount: '',
    paymentMethod: 'Transferencia',
    paymentRef: '',
    notes: '',
  });

  const summary = useMemo(() => {
    const total = payments.length;
    const paid = payments.filter((p) => p.status === 'paid').length;
    const pending = payments.filter((p) => p.status === 'pending').length;
    const overdue = payments.filter((p) => p.status === 'overdue').length;
    const skipped = payments.filter((p) => p.status === 'skipped').length;

    const totalAmount = payments.reduce((acc, p) => acc + Number(p?.bill?.amount || 0), 0);
    const paidAmount = payments.reduce(
  (acc, p) =>
    acc +
    (p.status === 'paid'
      ? Number(
          p.paidAmount ??
          (p?.bill?.amount ?? 0)
        )
      : 0),
  0
);


    return { total, paid, pending, overdue, skipped, totalAmount, paidAmount };
  }, [payments]);

  async function loadRules() {
    const data = await getPayableBills({}); // todas
    setRules(Array.isArray(data) ? data : []);
  }

  async function loadUpcoming() {
    const data = await getUpcoming(Number(upcomingDays || 14));
    setPayments(Array.isArray(data?.payments) ? data.payments : []);
  }

  async function loadWeek() {
    const data = await getDashboardWeek(weekDate);
    setPayments(Array.isArray(data?.payments) ? data.payments : []);
  }

  async function loadMonth() {
    const start = firstDayOfMonthISO(monthDate);
    const end = lastDayOfMonthISO(monthDate);
    const data = await getDashboardRange(start, end);
    setPayments(Array.isArray(data) ? data : []);
  }

  async function refreshCurrentTab() {
    setError('');
    setLoading(true);
    try {
      if (tab === TABS.RULES) {
        await loadRules();
      } else if (tab === TABS.UPCOMING) {
        await loadUpcoming();
      } else if (tab === TABS.WEEK) {
        await loadWeek();
      } else if (tab === TABS.MONTH) {
        await loadMonth();
      }
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Error cargando datos');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshCurrentTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // reload when filters change (solo en su tab)
  useEffect(() => {
    if (tab === TABS.UPCOMING) refreshCurrentTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingDays]);

  useEffect(() => {
    if (tab === TABS.WEEK) refreshCurrentTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekDate]);

  useEffect(() => {
    if (tab === TABS.MONTH) refreshCurrentTab();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthDate]);

  function openCreateBill() {
    setBillEditing(null);
    setBillForm({ ...emptyBillForm });
    setBillModalOpen(true);
  }

  function openEditBill(bill) {
    setBillEditing(bill);
    setBillForm({
      name: bill?.name || '',
      provider: bill?.provider || '',
      reference: bill?.reference || '',
      amount: bill?.amount ?? '',
      currency: bill?.currency || 'CRC',
      scheduleType: bill?.scheduleType || 'monthly',
      dueDate: bill?.dueDate || '',
      dayOfWeek: bill?.dayOfWeek || 1,
      dayOfMonth: bill?.dayOfMonth || 1,
      anchorDate: bill?.anchorDate || '',
      notes: bill?.notes || '',
      isActive: bill?.isActive ?? true,
    });
    setBillModalOpen(true);
  }

  function closeBillModal() {
    setBillModalOpen(false);
    setBillEditing(null);
  }

  function openPayModal(paymentRow) {
    setPayTarget(paymentRow);
    setPayForm({
      paidAmount: String(paymentRow?.bill?.amount ?? ''),
      paymentMethod: 'Transferencia',
      paymentRef: '',
      notes: '',
    });
    setPayModalOpen(true);
  }

  function closePayModal() {
    setPayModalOpen(false);
    setPayTarget(null);
  }

  async function handleSaveBill(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // payload limpio según scheduleType
      const payload = {
        name: billForm.name?.trim(),
        provider: billForm.provider?.trim() || null,
        reference: billForm.reference?.trim() || null,
        amount: Number(billForm.amount || 0),
        currency: billForm.currency,
        scheduleType: billForm.scheduleType,
        notes: billForm.notes?.trim() || null,
        isActive: !!billForm.isActive,
        dueDate: null,
        dayOfWeek: null,
        dayOfMonth: null,
        anchorDate: null,
      };

      if (!payload.name) throw new Error('El nombre es requerido');

      if (payload.scheduleType === 'one_time') {
        if (!billForm.dueDate) throw new Error('Seleccioná la fecha de vencimiento');
        payload.dueDate = billForm.dueDate;
      }

      if (payload.scheduleType === 'weekly') {
        payload.dayOfWeek = Number(billForm.dayOfWeek || 1);
      }

      if (payload.scheduleType === 'monthly') {
        payload.dayOfMonth = Number(billForm.dayOfMonth || 1);
      }

      if (payload.scheduleType === 'biweekly') {
        if (!billForm.anchorDate) throw new Error('Seleccioná la fecha ancla (inicio del ciclo quincenal)');
        payload.anchorDate = billForm.anchorDate;
      }

      if (billEditing?.id) {
        await updatePayableBill(billEditing.id, payload);
      } else {
        await createPayableBill(payload);
      }

      closeBillModal();
      // refrescar rules y también el tab actual
      await refreshCurrentTab();
      if (tab !== TABS.RULES) {
        await loadRules().catch(() => {});
      }
    } catch (e2) {
      setError(e2?.response?.data?.error || e2.message || 'No se pudo guardar');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleBill(id) {
    setError('');
    setLoading(true);
    try {
      await togglePayableBill(id);
      await loadRules();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'No se pudo actualizar');
    } finally {
      setLoading(false);
    }
  }

  async function handlePay(e) {
    e.preventDefault();
    if (!payTarget?.id) return;

    setError('');
    setLoading(true);
    try {
      await payBillPayment(payTarget.id, {
        paidAmount: Number(payForm.paidAmount || payTarget?.bill?.amount || 0),
        paymentMethod: payForm.paymentMethod || null,
        paymentRef: payForm.paymentRef?.trim() || null,
        notes: payForm.notes?.trim() || null,
      });

      closePayModal();
      await refreshCurrentTab();
    } catch (e2) {
      setError(e2?.response?.data?.error || e2.message || 'No se pudo marcar como pagado');
    } finally {
      setLoading(false);
    }
  }

  async function handleSkip(paymentId) {
    setError('');
    setLoading(true);
    try {
      await skipBillPayment(paymentId);
      await refreshCurrentTab();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'No se pudo omitir');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="pb-container">
      <div className="pb-header">
        <h1>Pago de Facturas</h1>

        <div className="pb-header-actions">
          <button className="pb-btn" onClick={openCreateBill} type="button">
            + Nueva Factura Recurrente
          </button>
          <button className="pb-btn pb-btn-outline" onClick={refreshCurrentTab} type="button">
            Refrescar
          </button>
        </div>
      </div>

      <div className="pb-tabs">
        <button className={`pb-tab ${tab === TABS.UPCOMING ? 'active' : ''}`} onClick={() => setTab(TABS.UPCOMING)}>
          Próximas a Vencer
        </button>
        <button className={`pb-tab ${tab === TABS.MONTH ? 'active' : ''}`} onClick={() => setTab(TABS.MONTH)}>
          Este Mes
        </button>
        <button className={`pb-tab ${tab === TABS.RULES ? 'active' : ''}`} onClick={() => setTab(TABS.RULES)}>
          Gestión de Facturas
        </button>
      </div>

      <div className="pb-toolbar">
        {tab === TABS.UPCOMING && (
          <div className="pb-toolbar-row">
            <label>Días:</label>
            <input
              type="number"
              min="1"
              max="90"
              value={upcomingDays}
              onChange={(e) => setUpcomingDays(e.target.value)}
            />
            <span className="pb-hint">Muestra ocurrencias y vencimientos de los próximos días.</span>
          </div>
        )}

        {tab === TABS.WEEK && (
          <div className="pb-toolbar-row">
            <label>Semana (elige un día):</label>
            <input type="date" value={weekDate} onChange={(e) => setWeekDate(e.target.value)} />
            <span className="pb-hint">Se agrupa de lunes a domingo automáticamente.</span>
          </div>
        )}

        {tab === TABS.MONTH && (
          <div className="pb-toolbar-row">
            <label>Mes:</label>
            <input type="month" value={monthDate.slice(0, 7)} onChange={(e) => setMonthDate(`${e.target.value}-01`)} />
            <span className="pb-hint">Ocurrencias del mes completo.</span>
          </div>
        )}
      </div>

      {error ? <div className="pb-error">{error}</div> : null}
      {loading ? <div className="pb-loading">Cargando…</div> : null}

      {/* Summary cards (solo para tabs con payments) */}
      {tab !== TABS.RULES && (
        <div className="pb-summary">
          <div className="pb-card">
            <div className="pb-card-title">Total</div>
            <div className="pb-card-value">{summary.total}</div>
            <div className="pb-card-sub">{formatMoney(summary.totalAmount, 'CRC')}</div>
          </div>
          <div className="pb-card">
            <div className="pb-card-title">Pagadas</div>
            <div className="pb-card-value">{summary.paid}</div>
            <div className="pb-card-sub">{formatMoney(summary.paidAmount, 'CRC')}</div>
          </div>
          <div className="pb-card">
            <div className="pb-card-title">Pendientes</div>
            <div className="pb-card-value">{summary.pending}</div>
          </div>
          <div className="pb-card">
            <div className="pb-card-title">Vencidas</div>
            <div className="pb-card-value">{summary.overdue}</div>
          </div>
          <div className="pb-card">
            <div className="pb-card-title">Omitidas</div>
            <div className="pb-card-value">{summary.skipped}</div>
          </div>
        </div>
      )}

      {/* Payments table */}
      {tab !== TABS.RULES && (
        <div className="pb-panel">
          <table className="pb-table">
            <thead>
              <tr>
                <th>Vence</th>
                <th>Factura</th>
                <th>Proveedor</th>
                <th>Monto</th>
                <th>Estado</th>
                <th className="pb-actions-col">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="6" className="pb-empty">
                    No hay facturas en este rango.
                  </td>
                </tr>
              ) : (
                payments.map((p) => (
                  <tr key={p.id}>
                    <td>{p.dueDate}</td>
                    <td>{p?.bill?.name || '-'}</td>
                    <td>{p?.bill?.provider || '-'}</td>
                    <td>{formatMoney(p?.bill?.amount, p?.bill?.currency)}</td>
                    <td>
                      <span className={statusClass(p.status)}>{statusLabel(p.status)}</span>
                    </td>
                    <td className="pb-actions">
                      <button
                        className="pb-btn-small"
                        type="button"
                        onClick={() => openPayModal(p)}
                        disabled={p.status === 'paid'}
                        title="Marcar como pagado"
                      >
                        Pagar
                      </button>
                      <button
                        className="pb-btn-small pb-btn-danger"
                        type="button"
                        onClick={() => handleSkip(p.id)}
                        disabled={p.status === 'paid'}
                        title="Omitir esta ocurrencia"
                      >
                        Omitir
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Rules management */}
      {tab === TABS.RULES && (
        <div className="pb-panel">
          <div className="pb-panel-head">
            <div className="pb-panel-title">Facturas Recurrentes</div>
            <div className="pb-panel-sub">Creá, editá o desactivá reglas..</div>
          </div>

          <table className="pb-table">
            <thead>
              <tr>
                <th>Nombre</th>
                <th>Proveedor</th>
                <th>Tipo</th>
                <th>Config</th>
                <th>Monto</th>
                <th>Estado</th>
                <th className="pb-actions-col">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 ? (
                <tr>
                  <td colSpan="7" className="pb-empty">
                    No hay reglas creadas.
                  </td>
                </tr>
              ) : (
                rules.map((b) => {
                  const cfg =
                    b.scheduleType === 'weekly'
                      ? `Cada ${DOW.find((x) => x.value === b.dayOfWeek)?.label || 'día'}`
                      : b.scheduleType === 'monthly'
                      ? `Día ${b.dayOfMonth}`
                      : b.scheduleType === 'biweekly'
                      ? `Ancla ${b.anchorDate || '-'}`
                      : `Vence ${b.dueDate || '-'}`;

                  return (
                    <tr key={b.id}>
                      <td>{b.name}</td>
                      <td>{b.provider || '-'}</td>
                      <td>{b.scheduleType}</td>
                      <td>{cfg}</td>
                      <td>{formatMoney(b.amount, b.currency)}</td>
                      <td>
                        <span className={`pb-badge ${b.isActive ? 'pb-active' : 'pb-inactive'}`}>
                          {b.isActive ? 'Activa' : 'Inactiva'}
                        </span>
                      </td>
                      <td className="pb-actions">
                        <button className="pb-btn-small" type="button" onClick={() => openEditBill(b)}>
                          Editar
                        </button>
                        <button className="pb-btn-small pb-btn-outline" type="button" onClick={() => handleToggleBill(b.id)}>
                          {b.isActive ? 'Desactivar' : 'Activar'}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal: Create/Edit Bill */}
      {billModalOpen && (
        <div className="pb-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && closeBillModal()}>
          <div className="pb-modal">
            <div className="pb-modal-title">{billEditing ? 'Editar Factura' : 'Nueva Factura Recurrente'}</div>

            <form className="pb-form" onSubmit={handleSaveBill}>
              <div className="pb-grid">
                <label>
                  Nombre *
                  <input
                    value={billForm.name}
                    onChange={(e) => setBillForm((s) => ({ ...s, name: e.target.value }))}
                    placeholder="Ej: Luz, Agua, Municipalidad..."
                    required
                  />
                </label>

                <label>
                  Proveedor
                  <input
                    value={billForm.provider}
                    onChange={(e) => setBillForm((s) => ({ ...s, provider: e.target.value }))}
                    placeholder="Ej: CNFL, AyA..."
                  />
                </label>

                <label>
                  Referencia / Contrato
                  <input
                    value={billForm.reference}
                    onChange={(e) => setBillForm((s) => ({ ...s, reference: e.target.value }))}
                    placeholder="NIS / Contrato / Clave"
                  />
                </label>

                <label>
                  Moneda
                  <select
                    value={billForm.currency}
                    onChange={(e) => setBillForm((s) => ({ ...s, currency: e.target.value }))}
                  >
                    <option value="CRC">CRC</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                  </select>
                </label>

                <label>
                  Monto
                  <input
                    type="number"
                    step="0.01"
                    value={billForm.amount}
                    onChange={(e) => setBillForm((s) => ({ ...s, amount: e.target.value }))}
                    placeholder="0.00"
                  />
                </label>

                <label>
                  Frecuencia
                  <select
                    value={billForm.scheduleType}
                    onChange={(e) => setBillForm((s) => ({ ...s, scheduleType: e.target.value }))}
                  >
                    <option value="weekly">Semanal</option>
                    <option value="biweekly">Quincenal (cada 14 días)</option>
                    <option value="monthly">Mensual</option>
                    <option value="one_time">Una vez</option>
                  </select>
                </label>

                {/* Config dinámica */}
                {billForm.scheduleType === 'weekly' && (
                  <label className="pb-span-2">
                    Día de pago (cada semana)
                    <select
                      value={billForm.dayOfWeek}
                      onChange={(e) => setBillForm((s) => ({ ...s, dayOfWeek: Number(e.target.value) }))}
                    >
                      {DOW.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {billForm.scheduleType === 'monthly' && (
                  <label className="pb-span-2">
                    Día del mes
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={billForm.dayOfMonth}
                      onChange={(e) => setBillForm((s) => ({ ...s, dayOfMonth: Number(e.target.value) }))}
                    />
                  </label>
                )}

                {billForm.scheduleType === 'biweekly' && (
                  <label className="pb-span-2">
                    Fecha ancla (inicio del ciclo)
                    <input
                      type="date"
                      value={billForm.anchorDate}
                      onChange={(e) => setBillForm((s) => ({ ...s, anchorDate: e.target.value }))}
                    />
                  </label>
                )}

                {billForm.scheduleType === 'one_time' && (
                  <label className="pb-span-2">
                    Fecha de vencimiento
                    <input
                      type="date"
                      value={billForm.dueDate}
                      onChange={(e) => setBillForm((s) => ({ ...s, dueDate: e.target.value }))}
                    />
                  </label>
                )}

                <label className="pb-span-2">
                  Notas
                  <textarea
                    rows="3"
                    value={billForm.notes}
                    onChange={(e) => setBillForm((s) => ({ ...s, notes: e.target.value }))}
                    placeholder="Opcional…"
                  />
                </label>

                <label className="pb-check pb-span-2">
                  <input
                    type="checkbox"
                    checked={!!billForm.isActive}
                    onChange={(e) => setBillForm((s) => ({ ...s, isActive: e.target.checked }))}
                  />
                  Activa
                </label>
              </div>

              <div className="pb-form-actions">
                <button className="pb-btn" type="submit" disabled={loading}>
                  Guardar
                </button>
                <button className="pb-btn pb-btn-outline" type="button" onClick={closeBillModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Pay */}
      {payModalOpen && payTarget && (
        <div className="pb-modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && closePayModal()}>
          <div className="pb-modal">
            <div className="pb-modal-title">Marcar como pagado</div>

            <div className="pb-pay-meta">
              <div><b>Factura:</b> {payTarget?.bill?.name || '-'}</div>
              <div><b>Vence:</b> {payTarget?.dueDate}</div>
            </div>

            <form className="pb-form" onSubmit={handlePay}>
              <div className="pb-grid">
                <label>
                  Monto pagado
                  <input
                    type="number"
                    step="0.01"
                    value={payForm.paidAmount}
                    onChange={(e) => setPayForm((s) => ({ ...s, paidAmount: e.target.value }))}
                  />
                </label>

                <label>
                  Método
                  <select
                    value={payForm.paymentMethod}
                    onChange={(e) => setPayForm((s) => ({ ...s, paymentMethod: e.target.value }))}
                  >
                    <option value="Transferencia">Transferencia</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Tarjeta">Tarjeta</option>
                    <option value="SINPE">SINPE</option>
                    <option value="Otro">Otro</option>
                  </select>
                </label>

                <label className="pb-span-2">
                  Referencia / Comprobante
                  <input
                    value={payForm.paymentRef}
                    onChange={(e) => setPayForm((s) => ({ ...s, paymentRef: e.target.value }))}
                    placeholder="Opcional…"
                  />
                </label>

                <label className="pb-span-2">
                  Notas
                  <textarea
                    rows="3"
                    value={payForm.notes}
                    onChange={(e) => setPayForm((s) => ({ ...s, notes: e.target.value }))}
                    placeholder="Opcional…"
                  />
                </label>
              </div>

              <div className="pb-form-actions">
                <button className="pb-btn" type="submit" disabled={loading}>
                  Confirmar Pago
                </button>
                <button className="pb-btn pb-btn-outline" type="button" onClick={closePayModal}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
